import {Component, EventEmitter, Input, OnChanges, OnInit, Output, Renderer2} from '@angular/core';
import {UntypedFormControl} from '@angular/forms';
import {custom} from 'devextreme/ui/dialog';
import ValidationEngine from 'devextreme/ui/validation_engine';
import {Subject} from 'rxjs';
import {takeUntil} from 'rxjs/operators';
import {ScreenService} from '../../../../../shared/services/general';
import {BillingInformation} from '../../../models/billing/billing-information';
import {PatientEditable} from '../../../models/order-entry/patient';

@Component({
  selector: 'app-edit-billing-information',
  templateUrl: './edit-billing-information.component.html',
  styleUrls: ['./edit-billing-information.component.scss']
})
export class EditBillingInformationComponent implements OnInit, OnChanges {
  private onDestroy$: Subject<void> = new Subject<void>();

  public get BillType(): string {
    return this.patientBillingInformation && this.patientBillingInformation.billType
      ? this.patientBillingInformation.billType : '';
  }

  public get Payer(): string {
    return this.patientBillingInformation && this.patientBillingInformation.payer
      ? this.patientBillingInformation.payer : '';
  }

  public get InsuranceId(): string {
    return this.patientBillingInformation && this.patientBillingInformation.insuranceId
      ? this.patientBillingInformation.insuranceId : '';
  }

  @Input() public popupVisible = false;
  @Input() public patientBillingInformation: BillingInformation = new BillingInformation();
  @Output() public closeEvent = new EventEmitter();
  @Output() public reloadPatientEvent = new EventEmitter();
  public addressControl: UntypedFormControl = new UntypedFormControl();
  public zipCodeControl: UntypedFormControl = new UntypedFormControl();
  public zipCodeExtraControl: UntypedFormControl = new UntypedFormControl();
  public today: string | number | Date = new Date();
  public phoneMaskRule = {X: /[0-9]/};
  public loadAddressInformation = new EventEmitter();
  public patientBillingInformationTemp: BillingInformation = new BillingInformation();
  public differentThanPatient: boolean | undefined | null = false;
  @Input() public patient?: PatientEditable = new PatientEditable();
  public hasInformation = false;
  public isPhoneOrTablet: boolean = false;

  public gender = ['MALE', 'FEMALE', 'UNKNOWN'];
  public relationshipWithInsured = ['FATHER', 'MOTHER', 'SON', 'DAUGHTER', 'SPOUSE', 'SELF'];

  constructor(private screen: ScreenService, private renderer: Renderer2) {
  }

  public ngOnInit(): void {
    setTimeout(() => {
      this.addressControl = new UntypedFormControl(
        this.patientBillingInformation ? this.patientBillingInformation.address : null
      );
      this.zipCodeControl = new UntypedFormControl(
        this.patientBillingInformation ? this.patientBillingInformation.zipCode : null
      );
      this.zipCodeExtraControl = new UntypedFormControl(
        this.patientBillingInformation ? this.patientBillingInformation.zipCodePlus4 : null
      );
    }, 500);

    this.isPhoneOrTablet = this.screen.sizes['screen-x-small'] || this.screen.sizes['screen-small'] || this.screen.sizes['screen-tablet'];
    this.screen.changed.pipe(takeUntil(this.onDestroy$)).subscribe(() => {
      this.isPhoneOrTablet = this.screen.sizes['screen-x-small'] || this.screen.sizes['screen-small'] || this.screen.sizes['screen-tablet'];
    });
  }

  public onShown() {
    ValidationEngine.validateGroup('billingInfo');
    ValidationEngine.validateGroup('address');
    this.updateAddressControl(false);
  }

  public close(): void {
    this.closeEvent.emit();
  }

  public differentThanPatientValueChanged(event: any) {
    if (!event.value) {
      this.assignInfoPatient(this.patient);
      this.updateAddressControl(false);
    } else {
      if (!this.hasInformation) {
        this.assignBillingInformation();
        this.updateAddressControl(true);
      }
      this.hasInformation = false;
    }
  }

  public ngOnChanges(changes: import('@angular/core').SimpleChanges): void {
    if (this.popupVisible && this.patientBillingInformation) {
      this.differentThanPatient = this.patientBillingInformation.differentThanPatient != undefined ? this.patientBillingInformation.differentThanPatient : false;
      this.assignBillingInformation(this.patientBillingInformation);
      this.updateAddressControl(false);
      if (this.differentThanPatient) {
        this.hasInformation = true;
      }
    }
  }

  public assignBillingInformation(billingInformation?: BillingInformation) {
    if (billingInformation) {
      this.patientBillingInformationTemp.lastName = billingInformation.lastName;
      this.patientBillingInformationTemp.firstName = billingInformation.firstName;
      this.patientBillingInformationTemp.middleName = billingInformation.middleName;
      if (billingInformation.birthDate) {
        this.patientBillingInformationTemp.birthDate = new Date(billingInformation.birthDate);
      } else {
        this.patientBillingInformationTemp.birthDate = '';
      }
      this.patientBillingInformationTemp.gender = billingInformation.gender;
      this.patientBillingInformationTemp.phone = billingInformation.phone;
      this.patientBillingInformationTemp.relationship = billingInformation.relationship;
      this.patientBillingInformationTemp.address = billingInformation.address;
      this.patientBillingInformationTemp.zipCode = billingInformation.zipCode;
      this.patientBillingInformationTemp.zipCodePlus4 = billingInformation.zipCodePlus4;
    } else {
      this.patientBillingInformationTemp.lastName = '';
      this.patientBillingInformationTemp.firstName = '';
      this.patientBillingInformationTemp.middleName = '';
      this.patientBillingInformationTemp.birthDate = '';
      this.patientBillingInformationTemp.gender = undefined;
      this.patientBillingInformationTemp.phone = '';
      this.patientBillingInformationTemp.relationship = undefined;
      this.patientBillingInformationTemp.address = undefined;
      this.patientBillingInformationTemp.zipCode = undefined;
      this.patientBillingInformationTemp.zipCodePlus4 = undefined;
    }
  }

  public assignInfoPatient(patient?: PatientEditable) {
    this.patientBillingInformationTemp.lastName = patient?.lastName ? patient.lastName : '';
    this.patientBillingInformationTemp.firstName = patient?.firstName ? patient.firstName : '';
    this.patientBillingInformationTemp.middleName = patient?.middleName ? patient.middleName : '';
    this.patientBillingInformationTemp.birthDate = (patient as any).dob !== null && (patient as any).dob !== undefined ? new Date((patient as any).dob) :
      patient?.birthDate !== null && patient?.birthDate !== undefined ? new Date(patient.birthDate) : '';
    this.patientBillingInformationTemp.gender = patient?.gender;
    this.patientBillingInformationTemp.phone = patient?.phone ? patient.phone : '';
    this.patientBillingInformationTemp.relationship = 'SELF';
    this.patientBillingInformationTemp.address = patient?.address;
    this.patientBillingInformationTemp.zipCode = patient?.zipCode;
    this.patientBillingInformationTemp.zipCodePlus4 = patient?.zipCodePlus4;
  }

  private updateAddressControl(clean: boolean) {
    if (clean) {
      this.addressControl?.setValue(null);
      this.zipCodeControl?.setValue(null);
      this.zipCodeExtraControl?.setValue('');
    } else {
      this.addressControl?.setValue(this.patientBillingInformationTemp.address);
      this.zipCodeControl?.setValue(this.patientBillingInformationTemp.zipCode && typeof this.patientBillingInformationTemp.zipCode === 'object' ?
        this.patientBillingInformationTemp.zipCode.code : this.patientBillingInformationTemp.zipCode);
      this.zipCodeExtraControl?.setValue(this.patientBillingInformationTemp.zipCodePlus4);
      this.loadAddressInformation.emit(true);
    }
  }

  public async save() {
    const patientData = ValidationEngine.validateGroup('billingInfo');
    const address = ValidationEngine.validateGroup('address');

    this.patientBillingInformation = this.patientBillingInformationTemp;
    this.patientBillingInformation.differentThanPatient = this.differentThanPatient ?? false;

    if (this.addressControl?.value) {
      this.patientBillingInformation.address = this.addressControl?.value;
    }

    if (this.zipCodeControl?.value) {
      this.patientBillingInformation.zipCode = this.zipCodeControl.value;
      this.patientBillingInformation.zipCodeId = this.zipCodeControl.value.id;
    }

    if (this.zipCodeExtraControl?.value) {
      this.patientBillingInformation.zipCodePlus4 = this.zipCodeExtraControl.value;
    }

    if (!patientData.isValid || !address.isValid) {
      const confirmationDialog = custom({
        title: 'Confirmation Save',
        messageHtml: 'Please note there is missing information for insurance claim, would you like to Save and continue later?',
        buttons: [{
          text: 'Save',
          onClick: (e) => {
            return e.component.option('text');
          }
        }, {
          text: 'Cancel',
          onClick: (e) => {
            return e.component.option('text');
          }
        }]
      });

      confirmationDialog.show().then((dialogResult: any) => {
        if (dialogResult === 'Cancel') {
          confirmationDialog.hide();
        } else {
          this.closeEvent.emit(this.patientBillingInformation);
        }
      });
    } else {
      this.closeEvent.emit(this.patientBillingInformation);
    }
  }

  public onContentReady(e: any) {
    const element = e.element;
    if (e.component._pickerType === 'native') {
      const button = element.querySelector('.dx-texteditor-buttons-container');
      const input = element.querySelector('.dx-texteditor-input');
      this.renderer.setStyle(button, 'display', 'none');
      this.renderer.setStyle(input, 'padding-right', '5px');
    }
  }
}
